import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';
import _ from 'lodash';

import { Input, Modal } from '@/common';
import { Button, IconButton, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { AuthContext } from '@/contexts';
import { CustomAttributeType, PERMISSION_TYPE } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getEnabledStatusFilterOptions } from '@/helpers';
import { formatDate } from '@/helpers/date';
import { showToast, ToastType } from '@/helpers/toast';
import { DeleteIcon, EditPenIcon } from '@/resources';
import {
  useDeleteUploaderUserMutation,
  useGetCustomAttributesByEventType,
  useGetEventByIdQuery,
  useUpdateUploaderUserMutation
} from '@/services';
import { CustomAttribute, UpdateUserBasic, User } from '@/types';

import { updateUploaderSchema } from '../Users/UserValidation';

type EventDetailProps = {
  organizationPermissions?: string[];
};

export const EventDetail = ({
  organizationPermissions: eventOrganizationPermissions
}: EventDetailProps) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const { permissions, organizationPermissions: userOrganizationPermissions } =
    useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;
  const hasFamilyPortalPermissionAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL
  );
  const hasCreateUploaderUserPermission = permissions.includes(
    PERMISSION_TYPE.CREATE_UPLOADER_USER
  );
  const hasUpdateUploaderPermission = permissions.includes(PERMISSION_TYPE.UPDATE_UPLOADER_USER);
  const hasDeleteUploaderPermission = permissions.includes(PERMISSION_TYPE.DELETE_UPLOADER_USER);
  const [isOpenDeleteUploaderModal, setIsOpenDeleteUploaderModal] = useState<boolean>(false);
  const [isOpenUpdateUploaderModal, setIsOpenUpdateUploaderModal] = useState<boolean>(false);
  const [selectedUploader, setSelectedUploader] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    data: eventData,
    isLoading: eventLoading,
    refetch: refetchEvent
  } = useGetEventByIdQuery(Number(eventId));

  const { data: customAttributes, isLoading: customAttributesLoading } =
    useGetCustomAttributesByEventType(Number(eventId), CustomAttributeType.CREATE_EDIT);

  const { mutate: deleteUploaderUser } = useDeleteUploaderUserMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteUploaderModal(false);
      setSelectedUploader(null);
      refetchEvent();
      showToast(ToastType.SUCCESS, t('userSuccessfullyDeleted'));
    },
    onError: () => {
      setIsOpenDeleteUploaderModal(false);
      showToast(ToastType.ERROR, t('userDeleteError'));
    }
  });

  const { mutate: updateUploaderUser } = useUpdateUploaderUserMutation(queryClient, {
    onSuccess: () => {
      setIsOpenUpdateUploaderModal(false);
      setSelectedUploader(null);
      setIsSubmitting(false);
      refetchEvent();
      showToast(ToastType.SUCCESS, t('userSuccessfullyUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, t(`${error.response.data.ErrorMessages[0].DisplayMessage}`));
    }
  });

  const handleDeleteUploader = (user: User) => {
    setSelectedUploader(user);
    setIsOpenDeleteUploaderModal(true);
  };

  const handleCloseDeleteUploaderModal = () => {
    setIsOpenDeleteUploaderModal(false);
    setSelectedUploader(null);
  };

  const handleUpdateUploader = (user: User) => {
    setSelectedUploader(user);
    setIsOpenUpdateUploaderModal(true);
  };
  const handleCloseUpdateUploaderModal = () => {
    setIsOpenUpdateUploaderModal(false);
    setSelectedUploader(null);
  };

  const getInitialValues = (user: User | null) => {
    const initialValues: UpdateUserBasic = {
      id: user?.id ?? 0,
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? ''
    };

    return initialValues;
  };

  const handleSubmit = (values: UpdateUserBasic) => {
    setIsSubmitting(true);
    const payload = {
      ...values
    };
    updateUploaderUser(payload);
  };

  // A null payload is not an event with empty fields: axiosOfflineInterceptor answers requests
  // with { data: null } when the API is unreachable, so the query resolves as a success and
  // isLoading goes false. The Event page swaps in its offline view once isOnline flips; until
  // then, rendering fields off a null event throws and takes down the whole route.
  const hasEventData = !!eventData?.data;

  return eventLoading || customAttributesLoading || !hasEventData ? (
    <Spinner />
  ) : (
    <div className='no-scrollbar mt-5 w-full overflow-auto md:mt-7 md:px-20'>
      <Label className='pb-5 md:pb-9' text={`${t('generalInformation')}`} type={LABEL_TYPE.H3} />
      <div className='flex flex-col gap-y-3 md:gap-y-4'>
        <div className='grid w-full grid-cols-2 md:grid-cols-5'>
          <>
            <Label
              className='mb-3 text-secondary-gray md:hidden'
              text={`${t('contentDeadline')}:`}
              type={LABEL_TYPE.PARAGRAPH1}
            />
            <Label
              text={`${eventData?.data && formatDate(new Date(eventData?.data.contentDeadline))}`}
              type={LABEL_TYPE.PARAGRAPH1}
              className='mb-4 justify-end !text-primary-black md:hidden'
            />
          </>
          <Label
            className='text-secondary-gray'
            text={`${t('organization')}:`}
            type={LABEL_TYPE.PARAGRAPH1}
          />
          <Label
            text={`${eventData?.data?.organization?.name ?? ''}`}
            type={LABEL_TYPE.PARAGRAPH1}
            className='justify-end !text-primary-black md:justify-start'
          />
          <>
            <Label
              className='ml-4 hidden text-secondary-gray md:block'
              text={`${t('availability')}:`}
              type={LABEL_TYPE.PARAGRAPH1}
            />
            <Label
              text={`${
                getEnabledStatusFilterOptions().find(
                  (option) => option.value === eventData?.data.enabled
                )?.label
              }`}
              type={LABEL_TYPE.PARAGRAPH1}
              className='hidden !text-primary-black md:block'
            />
          </>
        </div>
        <div className='grid w-full grid-cols-2 md:grid-cols-5'>
          <Label
            className='text-secondary-gray'
            text={`${t('dateOfCeremony')}:`}
            type={LABEL_TYPE.PARAGRAPH1}
          />
          <Label
            text={`${eventData?.data && formatDate(new Date(eventData?.data.startDateAndTime))}`}
            type={LABEL_TYPE.PARAGRAPH1}
            className='justify-end !text-primary-black md:justify-start'
          />
          <>
            <Label
              className='ml-4 hidden text-secondary-gray md:block'
              text={`${t('contentDeadline')}:`}
              type={LABEL_TYPE.PARAGRAPH1}
            />
            <Label
              text={`${eventData?.data && formatDate(new Date(eventData?.data.contentDeadline))}`}
              type={LABEL_TYPE.PARAGRAPH1}
              className='hidden !text-primary-black md:block'
            />
          </>
        </div>
        <div className='grid w-full grid-cols-2 md:grid-cols-5'>
          <div className='grid'>
            <Label
              className='text-secondary-gray'
              text={`${t('contentStructureTemplate')}:`}
              type={LABEL_TYPE.PARAGRAPH1}
            />
          </div>
          <div className='col-span-1 grid md:col-span-1'>
            <Label
              text={`${
                eventData?.data?.ceremonyTemplate?.name
                  ? t(eventData?.data?.ceremonyTemplate?.name)
                  : t('custom')
              }`}
              type={LABEL_TYPE.PARAGRAPH1}
              className='justify-end !text-primary-black md:justify-start'
            />
          </div>
          <div className='col-span-1 mt-3 grid md:col-span-1 md:ml-4 md:mt-0'>
            <Label
              className='text-secondary-gray'
              text={`${t('createdBy')}:`}
              type={LABEL_TYPE.PARAGRAPH1}
            />
          </div>
          <div className='col-span-1 mt-3 grid md:col-span-1 md:mt-0'>
            <Label
              text={eventData?.data.createdBy ?? ''}
              type={LABEL_TYPE.PARAGRAPH1}
              className='justify-end !text-primary-black md:justify-start'
            />
          </div>
        </div>
        <div className='grid w-full grid-cols-2 items-start justify-center md:hidden'>
          <Label
            className='text-secondary-gray'
            text={`${t('availability')}:`}
            type={LABEL_TYPE.PARAGRAPH1}
          />
          <Label
            text={`${
              getEnabledStatusFilterOptions().find(
                (option) => option.value === eventData?.data.enabled
              )?.label
            }`}
            type={LABEL_TYPE.PARAGRAPH1}
            className='justify-end !text-primary-black'
          />
        </div>
        <div className='grid w-full grid-cols-5 items-start justify-center pb-3.5'>
          <Label
            className='text-secondary-gray'
            text={`${t('description')}:`}
            type={LABEL_TYPE.PARAGRAPH1}
          />
          <div className='col-span-5 grid break-all md:col-span-4'>
            <Label
              className='mt-3 max-w-full !text-primary-black md:mt-0'
              text={`${eventData?.data.description}`}
              type={LABEL_TYPE.PARAGRAPH1}
            />
          </div>
        </div>
      </div>
      <div className='mt-5 border-b border-solid border-secondary-pale-blue-gray' />
      <Label className='py-7 md:py-9' text={`${t('personalInformation')}`} type={LABEL_TYPE.H3} />
      <div className='grid w-full grid-cols-1 gap-3 pb-3.5 md:grid-cols-3 md:gap-0'>
        {customAttributes?.data.map((ca: CustomAttribute, index: number) => (
          <div className='grid w-full grid-cols-2' key={index}>
            <Label
              className='text-secondary-gray'
              text={`${t(ca.label)}:`}
              type={LABEL_TYPE.PARAGRAPH1}
            />
            <Label
              text={ca.value}
              type={LABEL_TYPE.PARAGRAPH1}
              className='justify-end !text-primary-black md:justify-start'
            />
          </div>
        ))}
      </div>
      <div className='mt-5 border-b border-solid border-secondary-pale-blue-gray' />
      <div className='flex flex-row items-center justify-between'>
        <Label className='py-7 md:py-9' text={`${t('uploaders')}`} type={LABEL_TYPE.H3} />
        {hasCreateUploaderUserPermission && (
          <Button
            className='w-32'
            disabled={!hasFamilyPortalPermissionAccess}
            variant={ButtonVariant.SECONDARY}
            handleOnClick={() => {
              navigate(
                `/users/create?isUploader=true&organizationId=${eventData?.data.organization?.id}&eventId=${eventData?.data.id}`
              );
            }}>
            {t('addUploader')}
          </Button>
        )}
      </div>
      <div className='grid w-fit max-w-full grid-cols-1 pb-3.5'>
        {_.isEmpty(eventData?.data.users) ? (
          <Label
            text={t('noUploaders')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='mr-2 text-secondary-gray'
          />
        ) : (
          eventData?.data.users.map((user: User, index: number) => (
            <div
              key={user.id}
              className='grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 pb-3.5'>
              <Label
                text={`${index + 1}.`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='mr-2 text-secondary-gray'
              />
              <div className='min-w-0'>
                <span className='hidden md:flex'>
                  {user.firstName} {user.lastName} - {user.email}
                </span>
                <span className='md:hidden'>
                  {user.firstName} {user.lastName}
                  <Label text={user.email} />
                </span>
              </div>
              <div className='flex flex-row justify-end gap-2'>
                {hasUpdateUploaderPermission && (
                  <IconButton
                    variant={IconButtonVariant.SECONDARY}
                    handleOnClick={() => handleUpdateUploader(user)}>
                    <EditPenIcon className='text-primary-blue' />
                  </IconButton>
                )}
                {hasDeleteUploaderPermission && (
                  <IconButton
                    variant={IconButtonVariant.SECONDARY}
                    handleOnClick={() => handleDeleteUploader(user)}>
                    <DeleteIcon className='text-primary-blue' />
                  </IconButton>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {isOpenDeleteUploaderModal && (
        <ConfirmationModal
          onConfirm={() => selectedUploader?.id && deleteUploaderUser(selectedUploader.id)}
          isOpen={isOpenDeleteUploaderModal}
          onClose={handleCloseDeleteUploaderModal}
          message={t('deleteMessage', {
            item: `${selectedUploader?.firstName ?? ''} ${selectedUploader?.lastName ?? ''}`.trim()
          })}
        />
      )}
      {isOpenUpdateUploaderModal && (
        <Formik
          initialValues={getInitialValues(selectedUploader)}
          onSubmit={handleSubmit}
          validationSchema={updateUploaderSchema}>
          {({ errors, touched, dirty, handleSubmit }) => (
            <Modal
              isOpen={isOpenUpdateUploaderModal}
              onClose={handleCloseUpdateUploaderModal}
              height='300px'
              modalClasses='w-full md:w-[600px] h-[300px]'
              label={t('editUser')}
              hasButton
              buttonText={t('save')}
              buttonClasses='flex justify-end'
              hasCancelButton
              isButtonDisabled={isSubmitting || !dirty || Object.keys(errors).length > 0}
              handleSubmit={handleSubmit}>
              <Form>
                <div className='my-6 flex w-full flex-row gap-x-4'>
                  <div className='flex w-full flex-row gap-x-2'>
                    <Input
                      width='w-full'
                      id='firstName'
                      label={t('firstName')}
                      type='text'
                      isRequired
                      errorMessage={touched.firstName ? (errors.firstName as string) : undefined}
                      isInvalid={!!errors.firstName && touched.firstName}
                    />
                    <Input
                      width='w-full'
                      id='lastName'
                      label={t('lastName')}
                      type='text'
                      isRequired
                      errorMessage={touched.lastName ? (errors.lastName as string) : undefined}
                      isInvalid={!!errors.lastName && !!touched.lastName}
                    />
                  </div>
                </div>
              </Form>
            </Modal>
          )}
        </Formik>
      )}
    </div>
  );
};
